import { useState } from 'react'
import Button from '../../common/Button'
import StackedUsageBar from '../StackedUsageBar'
import { ValueSlot, BarSlot } from '../UsageSlots'
import { formatGb, formatGbPair, hasGbValue, formatNumber, formatPeriodLabel, projectEventsUsage, eventsUsageStatus, usageStatus, RESOURCE_COLORS } from '../../../utils/franchiseFormat'

const EMPTY_RESOURCE = { used_gb: 0, available_gb: 0, cached: false }
const EMPTY_EVENTS = { used: 0, limit: 0, available: 0, percentage_used: 0, period: '', cached: false }

/** Icon/color per projectEventsUsage() tone - kept separate from the util so it stays UI-free. */
const PROJECTION_STYLES = {
  info: {
    icon: 'info',
    className: 'text-gray-500 dark:text-gray-400',
    badgeClassName: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300',
  },
  ok: {
    icon: 'check_circle',
    className: 'text-emerald-600 dark:text-emerald-400',
    badgeClassName: 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400',
  },
  warning: {
    icon: 'warning',
    className: 'text-amber-600 dark:text-amber-400',
    badgeClassName: 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400',
  },
  critical: {
    icon: 'error',
    className: 'text-amber-700 dark:text-amber-400',
    badgeClassName: 'bg-amber-200 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300',
  },
}

/**
 * Discreet trigger + inline expandable panel with the "40% headroom" sizing
 * recommendation. Rendered only when the events footer flags the franchise
 * as close to/over its projected consumption (warning/critical tone) - kept
 * as an opt-in expand instead of always-on text so it doesn't compete with
 * the projection message itself for attention. Expands inline (not an
 * absolutely-positioned popover) so it can't get clipped by the modal
 * body's own scroll container.
 */
const RecommendationNote = ({ suggestedLimitLabel }) => {
  const [open, setOpen] = useState(false)
  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-400 dark:text-gray-500 hover:text-amber-600 dark:hover:text-amber-400 underline decoration-dotted underline-offset-2 transition-colors"
      >
        <span className="material-icons" style={{ fontSize: 12 }}>lightbulb</span>
        {open ? 'Ocultar recomendação' : 'Qual franquia é recomendada?'}
      </button>
      {open && (
        <div className="mt-2 rounded-md border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/30 p-2.5">
          <p className="text-[11px] font-semibold text-amber-800 dark:text-amber-300 leading-snug">
            Recomenda-se manter uma franquia mensal de eventos de pelo menos {suggestedLimitLabel} (40% acima da projeção deste mês), para eventuais janelas de picos de demanda, campanhas e/ou expansão operacional.
          </p>
        </div>
      )}
    </div>
  )
}

const CachedBadge = ({ cached, busy, hasError }) => {
  if (busy) return <span className="inline-block h-4 w-16 rounded bg-gray-100 dark:bg-gray-800 animate-pulse" />
  if (hasError) return null
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
        cached
          ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
          : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
      }`}
      title={cached ? 'Valor em cache (até 5 min)' : 'Calculado agora'}
    >
      <span className="material-icons" style={{ fontSize: 11 }}>{cached ? 'schedule' : 'bolt'}</span>
      {cached ? 'Em cache' : 'Ao vivo'}
    </span>
  )
}

const UsageMeterRow = ({ icon, title, subtitle, percentage, usedLabel, limitLabel, availableLabel, cached, busy, hasError, footer, projectionTone }) => {
  const status = eventsUsageStatus(percentage, projectionTone)
  const dimmed = busy || hasError
  return (
    <div className="rounded-lg border border-gray-100 dark:border-gray-800 p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${dimmed ? 'bg-gray-100 dark:bg-gray-800' : status.chip}`}>
            <span className={`material-icons ${dimmed ? 'text-gray-300 dark:text-gray-600' : status.text}`} style={{ fontSize: 17 }}>{icon}</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{title}</p>
            {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{subtitle}</p>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className={`text-sm font-bold tabular-nums ${dimmed ? '' : status.text}`}>
            <ValueSlot busy={busy} hasError={hasError} width="w-10">{percentage.toFixed(1)}%</ValueSlot>
          </span>
          <CachedBadge cached={cached} busy={busy} hasError={hasError} />
        </div>
      </div>

      <BarSlot busy={busy} hasError={hasError} heightClass="h-2">
        <div className={`h-2 rounded-full overflow-hidden ${status.track}`}>
          <div
            className={`h-full rounded-full ${status.bar} transition-all duration-500`}
            style={{ width: `${Math.min(Math.max(percentage, 1.5), 100)}%` }}
          />
        </div>
      </BarSlot>

      <div className="mt-2.5 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>
          <strong className="text-gray-700 dark:text-gray-300 font-semibold">
            <ValueSlot busy={busy} hasError={hasError} width="w-10">{usedLabel}</ValueSlot>
          </strong>{' '}
          usado de <ValueSlot busy={busy} hasError={hasError} width="w-10">{limitLabel}</ValueSlot>
        </span>
        <span><ValueSlot busy={busy} hasError={hasError} width="w-12">{availableLabel}</ValueSlot> disponível</span>
      </div>

      {footer && !busy && !hasError && (
        <div className="mt-3 pt-2.5 border-t border-gray-100 dark:border-gray-800">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${footer.badgeClassName}`}>
            <span className="material-icons" style={{ fontSize: 12 }}>{footer.icon}</span>
            Importante
          </span>
          <p className={`mt-1.5 text-xs leading-snug ${footer.className}`}>{footer.message}</p>
          {footer.showRecommendation && <RecommendationNote suggestedLimitLabel={footer.suggestedLimitLabel} />}
        </div>
      )}
    </div>
  )
}

/** Database and storage share one quota, so they render as one stacked bar (2 identity segments) instead of 2 bars. */
const StorageUsageRow = ({ database, storage, limitGb, busy, hasError }) => {
  const usedGb = database.used_gb + storage.used_gb
  const availableGb = Math.max(limitGb - usedGb, 0)
  const percentage = limitGb > 0 ? Math.min((usedGb / limitGb) * 100, 100) : 0
  const status = usageStatus(percentage)
  const dimmed = busy || hasError
  // Hide a resource that would render as "0.00 GB"; keep both while loading/on
  // error and when both are empty so the list never vanishes.
  const bothEmpty = !hasGbValue(database.used_gb) && !hasGbValue(storage.used_gb)
  const showDb = busy || hasError || bothEmpty || hasGbValue(database.used_gb)
  const showStorage = busy || hasError || bothEmpty || hasGbValue(storage.used_gb)

  return (
    <div className="rounded-lg border border-gray-100 dark:border-gray-800 p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${dimmed ? 'bg-gray-100 dark:bg-gray-800' : status.chip}`}>
            <span className={`material-icons ${dimmed ? 'text-gray-300 dark:text-gray-600' : status.text}`} style={{ fontSize: 17 }}>data_usage</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">Armazenamento</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 truncate">Banco de dados + arquivos, mesma franquia</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-0.5 shrink-0">
          <span className={`text-sm font-bold tabular-nums ${dimmed ? '' : status.text}`}>
            <ValueSlot busy={busy} hasError={hasError} width="w-10">{percentage.toFixed(1)}%</ValueSlot>
          </span>
          <span className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
            <ValueSlot busy={busy} hasError={hasError} width="w-16">{formatGbPair(usedGb, limitGb)}</ValueSlot>
          </span>
        </div>
      </div>

      <BarSlot busy={busy} hasError={hasError} heightClass="h-2">
        <StackedUsageBar
          heightClass="h-2"
          total={limitGb}
          segments={[
            { value: database.used_gb, colorClass: RESOURCE_COLORS.database.bar },
            { value: storage.used_gb, colorClass: RESOURCE_COLORS.storage.bar },
          ]}
        />
      </BarSlot>

      <div className="mt-3 space-y-1.5">
        {showDb && (
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
              <span className={`w-2 h-2 rounded-full ${RESOURCE_COLORS.database.dot}`} />
              Banco de dados
            </span>
            <span className="flex items-center gap-2">
              <span className="text-gray-500 dark:text-gray-400 tabular-nums">
                <ValueSlot busy={busy} hasError={hasError} width="w-10">{formatGb(database.used_gb)}</ValueSlot>
              </span>
              <CachedBadge cached={database.cached} busy={busy} hasError={hasError} />
            </span>
          </div>
        )}
        {showStorage && (
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
              <span className={`w-2 h-2 rounded-full ${RESOURCE_COLORS.storage.dot}`} />
              Arquivos
            </span>
            <span className="flex items-center gap-2">
              <span className="text-gray-500 dark:text-gray-400 tabular-nums">
                <ValueSlot busy={busy} hasError={hasError} width="w-10">{formatGb(storage.used_gb)}</ValueSlot>
              </span>
              <CachedBadge cached={storage.cached} busy={busy} hasError={hasError} />
            </span>
          </div>
        )}
      </div>

      <div className="mt-2.5 pt-2.5 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>
          <strong className="text-gray-700 dark:text-gray-300 font-semibold">
            <ValueSlot busy={busy} hasError={hasError} width="w-12">{formatGb(usedGb)}</ValueSlot>
          </strong>{' '}
          usado de <ValueSlot busy={busy} hasError={hasError} width="w-10">{formatGb(limitGb)}</ValueSlot>
        </span>
        <span><ValueSlot busy={busy} hasError={hasError} width="w-14">{formatGb(availableGb)}</ValueSlot> disponível</span>
      </div>
    </div>
  )
}

const FranchiseUsageModal = ({ isOpen, onClose, usage, loading, onRefresh }) => {
  if (!isOpen) return null

  const busy = loading
  const hasError = !loading && !usage
  const safeUsage = usage || {
    connectors: 0,
    limit_gb: 0,
    database: EMPTY_RESOURCE,
    storage: EMPTY_RESOURCE,
    events: EMPTY_EVENTS,
  }

  const eventsProjection = !busy && !hasError ? projectEventsUsage(safeUsage.events) : null
  const eventsFooter = eventsProjection
    ? {
        ...PROJECTION_STYLES[eventsProjection.tone],
        message: eventsProjection.message,
        showRecommendation: eventsProjection.tone === 'warning' || eventsProjection.tone === 'critical',
        suggestedLimitLabel: eventsProjection.suggestedLimitLabel,
      }
    : null

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-8">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-2xl max-h-[calc(100vh-4rem)] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex-shrink-0 p-2 rounded-lg bg-blue-100 dark:bg-blue-900/40">
                <span className="material-icons text-blue-600 dark:text-blue-400" style={{ fontSize: 20 }}>workspace_premium</span>
              </div>
              <div className="min-w-0">
                <h2 className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                  Franquia da Empresa
                  {hasError && (
                    <span
                      className="material-icons text-red-500 dark:text-red-400"
                      style={{ fontSize: 18 }}
                      title="Não foi possível carregar os dados da franquia"
                    >
                      error_outline
                    </span>
                  )}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">Uso dos recursos contratados</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition"
              title="Fechar"
            >
              <span className="material-icons" style={{ fontSize: 20 }}>close</span>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto min-h-0 px-6 py-5 space-y-4">
          {/* Connectors stat tile */}
          <div className="flex items-center gap-3 rounded-lg border border-indigo-100 dark:border-indigo-900/40 bg-indigo-50/60 dark:bg-indigo-950/20 p-4">
            <div className="shrink-0 w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
              <span className="material-icons text-indigo-600 dark:text-indigo-400" style={{ fontSize: 20 }}>bolt</span>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white leading-none tabular-nums">
                <ValueSlot busy={busy} hasError={hasError} width="w-8" heightClass="h-5">{safeUsage.connectors}</ValueSlot>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {safeUsage.connectors === 1 ? 'Conector ativo' : 'Conectores ativos'}
              </p>
            </div>
          </div>

          <StorageUsageRow
            database={safeUsage.database}
            storage={safeUsage.storage}
            limitGb={safeUsage.limit_gb}
            busy={busy}
            hasError={hasError}
          />

          <UsageMeterRow
            icon="event_repeat"
            title="Eventos no mês"
            subtitle={hasError ? 'Período indisponível' : `Período: ${formatPeriodLabel(safeUsage.events.period)}`}
            percentage={safeUsage.events.percentage_used}
            usedLabel={formatNumber(safeUsage.events.used)}
            limitLabel={formatNumber(safeUsage.events.limit)}
            availableLabel={formatNumber(safeUsage.events.available)}
            cached={safeUsage.events.cached}
            busy={busy}
            hasError={hasError}
            footer={eventsFooter}
            projectionTone={eventsProjection?.tone}
          />

          <p className="text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-1.5 pt-1">
            <span className="material-icons" style={{ fontSize: 12 }}>info</span>
            {busy || hasError
              ? 'Banco e armazenamento usam o mesmo limite. Eventos no mês sempre reiniciam no dia 1º.'
              : `Banco e armazenamento usam o mesmo limite de ${formatGb(safeUsage.limit_gb)}. Eventos no mês sempre reiniciam no dia 1º.`}
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex-shrink-0 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onRefresh} loading={loading}>
            <span className="material-icons" style={{ fontSize: 16 }}>refresh</span>
            Atualizar
          </Button>
          <Button variant="primary" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </div>
  )
}

export default FranchiseUsageModal
